/**
 * The add / edit form for one ILI anomaly. Sends snake_case in the tenant's
 * units; a cleared field sends `null` on an edit, and nothing on a create.
 */
import React, { useState } from 'react';
import type { PfCatalog, PfMeta, PfSite } from '../../../types/pipeline-facility';
import type { PfAnomaly, PfAnomalyWrite } from '../../../types/pf-digs';
import { blankToNull, toInput } from '../../../lib/pipeline-facility';
import { numOrNull } from '../../../lib/pf-digs';
import { PF_DATE_INPUT, PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfField, PfFormError } from '../PfBits';
import { PfFormGrid, unitLabel } from './DigBits';

export function AnomalyForm({
  anomaly,
  sites,
  defaultSiteId,
  catalog,
  meta,
  pending,
  error,
  errorAction,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  anomaly?: PfAnomaly | null;
  sites: PfSite[];
  defaultSiteId?: string;
  catalog: PfCatalog | null;
  meta: PfMeta | null;
  pending: boolean;
  error: string | null;
  errorAction?: React.ReactNode;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: PfAnomalyWrite) => void;
}) {
  const u = meta?.units ?? null;
  const [siteId, setSiteId] = useState(anomaly?.siteId ?? defaultSiteId ?? '');
  const [f, setF] = useState({
    feature_id: anomaly?.featureId ?? '',
    feature_type: anomaly?.featureType ?? '',
    chainage: toInput(anomaly?.chainage),
    odometer: toInput(anomaly?.odometer),
    clock_position: anomaly?.clockPosition ?? '',
    depth_pct_wt: toInput(anomaly?.depthPctWt),
    length: toInput(anomaly?.length),
    width: toInput(anomaly?.width),
    latitude: toInput(anomaly?.latitude),
    longitude: toInput(anomaly?.longitude),
    ili_vendor: anomaly?.iliVendor ?? '',
    ili_run_date: anomaly?.iliRunDate ?? '',
    priority: anomaly?.priority ?? '',
    notes: anomaly?.notes ?? '',
  });
  const [local, setLocal] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocal(null);
    if (!anomaly && !siteId) return setLocal('Choose the site the anomaly is on.');
    if (!f.feature_id.trim()) return setLocal('Enter the feature ID from the ILI list.');
    if (!f.feature_type) return setLocal('Choose the feature type.');
    const nums: Record<string, number | null> = {};
    for (const k of ['chainage', 'odometer', 'depth_pct_wt', 'length', 'width', 'latitude', 'longitude'] as const) {
      const n = numOrNull(f[k]);
      if (n !== null && !Number.isFinite(n)) return setLocal(`${k.replace(/_/g, ' ')} must be a number.`);
      nums[k] = n;
    }
    const body: PfAnomalyWrite = {
      feature_id: f.feature_id.trim(),
      feature_type: f.feature_type,
      clock_position: blankToNull(f.clock_position),
      ili_vendor: blankToNull(f.ili_vendor),
      ili_run_date: blankToNull(f.ili_run_date),
      priority: blankToNull(f.priority),
      notes: blankToNull(f.notes),
      ...nums,
    };
    if (!anomaly) {
      body.site_id = siteId;
      // Absent is not zero: a create sends only what was entered.
      (Object.keys(body) as (keyof PfAnomalyWrite)[]).forEach((k) => {
        if (body[k] === null) delete body[k];
      });
    }
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="pf-anomaly-form">
      <PfFormError message={local ?? error} />
      {!local && error && errorAction}
      <PfFormGrid>
        {!anomaly && (
          <PfField label="Site">
            <select className={PF_INPUT} aria-label="Site" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">Choose a site</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </PfField>
        )}
        <PfField label="Feature ID">
          <input className={PF_INPUT} aria-label="Feature ID" value={f.feature_id} onChange={set('feature_id')} />
        </PfField>
        <PfField label="Feature type">
          <select className={PF_INPUT} aria-label="Feature type" value={f.feature_type} onChange={set('feature_type')}>
            <option value="">Choose</option>
            {(catalog?.featureTypes ?? []).map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label={unitLabel('Chainage', u?.chainage)}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="Chainage" value={f.chainage} onChange={set('chainage')} />
        </PfField>
        <PfField label={unitLabel('Odometer', u?.chainage)}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="Odometer" value={f.odometer} onChange={set('odometer')} />
        </PfField>
        <PfField label="Clock position" hint="1:00 to 12:59">
          <input className={PF_INPUT} aria-label="Clock position" placeholder="3:30" value={f.clock_position} onChange={set('clock_position')} />
        </PfField>
        <PfField label="Depth (% WT)">
          <input className={PF_INPUT} inputMode="decimal" aria-label="Depth % WT" value={f.depth_pct_wt} onChange={set('depth_pct_wt')} />
        </PfField>
        <PfField label={unitLabel('Length', u?.wallThickness)}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="Length" value={f.length} onChange={set('length')} />
        </PfField>
        <PfField label={unitLabel('Width', u?.wallThickness)}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="Width" value={f.width} onChange={set('width')} />
        </PfField>
        <PfField label="Priority">
          <input className={PF_INPUT} aria-label="Priority" value={f.priority} onChange={set('priority')} />
        </PfField>
        <PfField label="ILI vendor">
          <input className={PF_INPUT} aria-label="ILI vendor" value={f.ili_vendor} onChange={set('ili_vendor')} />
        </PfField>
        <PfField label="ILI run date">
          <input type="date" className={PF_DATE_INPUT} aria-label="ILI run date" value={f.ili_run_date} onChange={set('ili_run_date')} />
        </PfField>
        <PfField label="Latitude">
          <input className={PF_INPUT} inputMode="decimal" aria-label="Latitude" value={f.latitude} onChange={set('latitude')} />
        </PfField>
        <PfField label="Longitude">
          <input className={PF_INPUT} inputMode="decimal" aria-label="Longitude" value={f.longitude} onChange={set('longitude')} />
        </PfField>
      </PfFormGrid>
      <PfField label="Notes">
        <textarea className={`${PF_INPUT} h-20 py-2`} aria-label="Notes" value={f.notes} onChange={set('notes')} />
      </PfField>
      <div className="flex justify-end gap-2">
        <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={PF_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
